import { useState, useCallback } from 'react';
import { Form, notification } from 'antd';

/**
 * Custom hook for ClientInform modal forms
 * Handles form instance, validation, bulk operations, and submission state
 * 
 * @param {Array} selectedUnits - Array of selected building units
 * @param {Array} requiredFields - Array of required field names to validate
 * @returns {Object} Form utilities and state
 */
const useClientInformForm = (selectedUnits, requiredFields = []) => {
  const [form] = Form.useForm();
  const [isValid, setIsValid] = useState(false);
  const [emailPreview, setEmailPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  /**
   * Validate that all selected units have required fields filled
   * Shows error notification if validation fails
   * @returns {boolean} - true if valid, false otherwise
   */
  const validateUnits = useCallback(() => {
    if (!selectedUnits || selectedUnits.length === 0) {
      notification.error({
        message: 'Chybějící data',
        description: 'Nejsou vybrány žádné jednotky',
      });
      return false;
    }

    const missingData = selectedUnits.filter(unit => {
      const missingFields = requiredFields.filter(field => {
        // Check in multiple possible locations
        let value = unit[field];
        
        // If not found directly, check in building_unit
        if (!value && unit.building_unit) {
          value = unit.building_unit[field];
        }
        
        // If still not found, check in service_rrules (for rrule)
        if (!value && field === 'rrule' && unit.building_unit?.service_rrules) {
          const lastRule = unit.building_unit.service_rrules.at(-1);
          value = lastRule?.rrule;
        }
        
        // For substitute fields, check directly on unit
        if (!value && field.startsWith('substitute_')) {
          value = unit[field];
        }
        
        return !value;
      });
      
      return missingFields.length > 0;
    });
    
    if (missingData.length > 0) {
      notification.error({
        message: 'Chybí povinná data',
        description: 'Nutno vyplnit záznamy',
      });
      return false;
    }

    return true;
  }, [selectedUnits, requiredFields]);

  /**
   * Apply bulk time range to all selected units
   * @param {Array} dates - Array of [startDate, endDate] from RangePicker
   */
  const applyBulkTimeRange = useCallback((dates) => {
    if (!dates || dates.length !== 2) return;
    
    selectedUnits.forEach(unit => {
      form.setFieldValue(['change_week', unit.id], dates);
    });
  }, [selectedUnits, form]);

  /**
   * Apply bulk compensation dates to all selected units (for rescheduling modal)
   * @param {Array} dates - Dates from MultiDatePicker
   */
  const applyBulkCompensationDate = useCallback((dates) => {
    if (!dates?.some(Boolean)) return;

    selectedUnits.forEach(unit => {
      form.setFieldValue(['compensation_date', unit.id], [...dates]);
    });
  }, [selectedUnits, form]);

  /**
   * Validate form and proceed to confirmation step
   * @returns {Promise<boolean>}
   */
  const validateAndProceed = useCallback(async () => {
    try {
      await form.validateFields();
      setShowConfirmation(true);
      return true;
    } catch (error) {
      notification.error({
        message: 'Chybné vyplnění formuláře',
        description: 'Prosím vyplňte všechna povinná pole',
      });
      return false;
    }
  }, [form]);

  /**
   * Reset form and confirmation state
   */
  const resetForm = useCallback(() => {
    form.resetFields();
    setShowConfirmation(false);
    setEmailPreview('');
    setIsSubmitting(false);
    setIsValid(false);
  }, [form]);

  /**
   * Go back from confirmation to form editing
   */
  const goBackToForm = useCallback(() => {
    setShowConfirmation(false);
    setEmailPreview('');
  }, []);

  return {
    form,
    isValid,
    emailPreview,
    isSubmitting,
    showConfirmation,
    setIsValid,
    setEmailPreview,
    setIsSubmitting,
    setShowConfirmation,
    validateUnits,
    applyBulkTimeRange,
    applyBulkCompensationDate,
    validateAndProceed,
    resetForm,
    goBackToForm,
  };
};

export default useClientInformForm;
